import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight } from "lucide-react";
import { FadeUp } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const achievements = [
  { emoji: "🐝", name: "Hive Builder", req: "1 qualified referral" },
  { emoji: "🐝", name: "Apiary Advocate", req: "5 qualified referrals" },
  { emoji: "🐝", name: "Community Builder", req: "10 qualified referrals" },
  { emoji: "🏆", name: "ApiaryKeeper Ambassador", req: "25 qualified referrals" },
];

export default function ReferralRewards() {
  const [referral, setReferral] = useState(null);

  useEffect(() => {
    axios.get(`${API}/settings/public`).then((r) => setReferral(r.data.referral)).catch(() => {});
  }, []);

  return (
    <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="referral-rewards-section">
      <div className="grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-6">
          <FadeUp>
            <p className="text-xs uppercase tracking-[0.3em] text-honey-dark mb-6">Referral Rewards</p>
            <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tighter text-forest leading-[1.02] mb-6" data-testid="referral-rewards-headline">
              Refer a Fellow Beekeeper 🐝
            </h2>
            <p className="text-ink/60 leading-relaxed max-w-lg mb-6">
              When another beekeeper joins ApiaryKeeper using your referral link or code and
              completes the qualifying steps, you'll earn BeeCredits™ you can use toward
              your subscription.
            </p>
            {referral && referral.enabled && (
              <p className="inline-block bg-honey/15 border border-honey/50 text-forest text-sm font-semibold px-5 py-2.5 rounded-full" data-testid="referral-current-reward">
                Current Reward: Earn {referral.reward_amount} BeeCredits™ for every qualified referral
              </p>
            )}
            {referral && !referral.enabled && (
              <p className="inline-block bg-forest/5 border border-forest/15 text-ink/50 text-sm font-medium px-5 py-2.5 rounded-full" data-testid="referral-paused-note">
                Referral rewards are launching soon.
              </p>
            )}
            <div className="mt-8">
              <Link
                to="/pre-register?src=referral"
                data-testid="referral-cta"
                className="group inline-flex items-center gap-2 text-sm font-semibold text-forest border-b border-honey pb-1 hover:text-honey-dark transition-colors duration-300"
              >
                Join the list to get your referral code
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </FadeUp>
        </div>
        <div className="lg:col-span-6">
          <FadeUp delay={0.15}>
            <div className="bg-white border border-forest/10 rounded-3xl p-8 md:p-10" data-testid="referral-achievements">
              <p className="text-xs uppercase tracking-[0.25em] text-ink/40 mb-6">Referral achievements</p>
              <div className="flex flex-col divide-y divide-forest/10">
                {achievements.map((a) => (
                  <div key={a.name} className="flex items-center justify-between py-4" data-testid={`achievement-${a.name.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}>
                    <span className="flex items-center gap-3">
                      <span className="text-xl">{a.emoji}</span>
                      <span className="font-display text-xl text-forest">{a.name}</span>
                    </span>
                    <span className="text-sm text-ink/50">{a.req}</span>
                  </div>
                ))}
              </div>
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}
